// Which documents count. Corrections replace forms only through explicit, confirmed links; upload order never decides.
import type { Module } from "./issues.ts";
import { IssueList } from "./issues.ts";
import type { Correction, Decision, Id, InfoReturnKind, Source, YearFile } from "./types.ts";
import { live } from "./yearfile.ts";

export type ExclusionReason =
  | "wrong_year"
  | "superseded"
  | "voided"
  | "replacement_pending"
  | "corrected_unlinked"
  | "chain_unresolved"
  | "duplicate_unresolved"
  | "unassigned";

export type InfoReturn = Extract<Source, { kind: InfoReturnKind }>;

export interface SourceView {
  /** Every live source, sorted by id. */
  all: Source[];
  /** Information returns that count, after corrections. */
  effectiveForms: InfoReturn[];
  excluded: { sourceId: Id; reason: ExclusionReason }[];
}

const INFO_KINDS: readonly string[] = ["form_1099b", "form_1099nec", "form_1099misc", "form_1099k"];

export function isInfoReturn(s: Source): s is InfoReturn {
  return INFO_KINDS.includes(s.kind);
}

export function moduleForSource(s: Source): Module {
  return s.kind === "form_1099b" || s.kind === "broker_statement" ? "section1256" : "propBusiness";
}

const FORM_NAME: Record<InfoReturnKind, string> = {
  form_1099b: "1099-B",
  form_1099nec: "1099-NEC",
  form_1099misc: "1099-MISC",
  form_1099k: "1099-K",
};

export function formName(s: InfoReturn): string {
  return `${s.issuer || "Unnamed issuer"} ${FORM_NAME[s.kind]}${s.correctedAsPrinted ? " (corrected)" : ""}`;
}

export function byId<T extends { id: Id }>(list: T[]): T[] {
  return [...list].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** Decisions that pin revisions are only valid while those records are unchanged. */
export function decisionIsCurrent(d: Decision, yf: YearFile): boolean {
  const rev = (list: { id: Id; revision: number; deletedAt: string | null }[], id: Id) => list.find((r) => r.id === id && r.deletedAt === null)?.revision;
  switch (d.kind) {
    case "section1256_basis":
      return d.sourceId === null || rev(yf.sources, d.sourceId) === d.sourceRevision;
    case "confirm_both_info_returns":
      return d.sourceIds.every((id, i) => rev(yf.sources, id) === d.revisions[i]);
    case "corrected_form_only":
      return rev(yf.sources, d.sourceId) === d.sourceRevision;
    case "recognition_year":
      return rev(yf.events, d.eventId) === d.eventRevision;
    case "dismiss_duplicate":
      return (
        (rev(yf.events, d.a) ?? rev(yf.sources, d.a)) === d.aRevision && (rev(yf.events, d.b) ?? rev(yf.sources, d.b)) === d.bRevision
      );
    case "accept_variance":
      return (rev(yf.sources, d.subjectId) ?? rev(yf.events, d.subjectId) ?? rev(yf.firms, d.subjectId)) === d.subjectRevision;
    case "prop_income_basis":
    case "fee_treatment":
      return true;
  }
}

/** Live decisions, split into those still valid and those a later edit has made stale. */
export function currentDecisions(yf: YearFile, issues: IssueList): Decision[] {
  const out: Decision[] = [];
  for (const d of byId(live(yf.decisions))) {
    if (decisionIsCurrent(d, yf)) out.push(d);
    else
      issues.add({
        code: "DECISION_STALE",
        severity: "review",
        module: "file",
        personId: null,
        refs: [d.id],
        message: "A record changed after you made a decision about it, so that decision no longer applies.",
        nextAction: "Review the changed record and decide again.",
      });
  }
  return out;
}

function sameScope(a: InfoReturn, b: InfoReturn): boolean {
  if (a.kind !== b.kind || a.taxYear !== b.taxYear || a.personId !== b.personId) return false;
  if (a.kind === "form_1099b" && b.kind === "form_1099b")
    return a.accountId !== null && a.accountId === b.accountId && a.description.trim().toLowerCase() === b.description.trim().toLowerCase();
  return a.firmId !== null && a.firmId === b.firmId;
}

function correctionCurrent(c: Correction, forms: Map<Id, InfoReturn>): boolean {
  if (c.status !== "confirmed" || !c.confirmedRevisions) return false;
  const o = forms.get(c.originalId);
  const r = c.replacementId === null ? null : forms.get(c.replacementId);
  return o?.revision === c.confirmedRevisions.original && (c.replacementId === null ? c.confirmedRevisions.replacement === null : r?.revision === c.confirmedRevisions.replacement);
}

export function resolveSources(yf: YearFile, decisions: Decision[], issues: IssueList): SourceView {
  const all = byId(live(yf.sources));
  const excluded = new Map<Id, ExclusionReason>();
  const exclude = (id: Id, reason: ExclusionReason) => {
    if (!excluded.has(id)) excluded.set(id, reason);
  };

  const forms = all.filter(isInfoReturn);
  const formById = new Map(forms.map((f) => [f.id, f]));

  for (const f of forms) {
    if (f.taxYear !== yf.taxYear) {
      exclude(f.id, "wrong_year");
      issues.add({
        code: "WRONG_YEAR",
        severity: "review",
        module: moduleForSource(f),
        personId: f.personId,
        refs: [f.id],
        message: `${formName(f)} is for ${f.taxYear}, not ${yf.taxYear}, so it isn't counted.`,
        nextAction: `Remove it, or fix the year if it was entered wrong.`,
      });
    }
    const unassigned = f.kind === "form_1099b" ? f.accountId === null : f.firmId === null;
    if (f.personId === null || unassigned) {
      exclude(f.id, "unassigned");
      issues.add({
        code: "FORM_UNASSIGNED",
        severity: "blocker",
        module: moduleForSource(f),
        personId: f.personId,
        refs: [f.id],
        message: `${formName(f)} isn't tied to ${f.personId === null ? "a person" : f.kind === "form_1099b" ? "an account" : "a prop firm"} yet.`,
        nextAction: f.personId === null ? "Choose whose form this is." : f.kind === "form_1099b" ? "Choose the futures account it covers." : "Choose the prop firm that paid you.",
      });
    }
  }

  // Corrections: validate each, then check the graph for forks and cycles.
  const corrections = byId(live(yf.corrections)).filter((c) => c.status !== "rejected");
  const valid: Correction[] = [];
  for (const c of corrections) {
    const o = formById.get(c.originalId);
    const r = c.replacementId === null ? null : formById.get(c.replacementId);
    const bad = !o || (c.replacementId !== null && (!r || c.replacementId === c.originalId || !sameScope(o, r)));
    if (bad) {
      issues.add({
        code: "CORRECTION_SCOPE_MISMATCH",
        severity: "review",
        module: o ? moduleForSource(o) : "file",
        personId: o?.personId ?? null,
        refs: [c.id, c.originalId, ...(c.replacementId ? [c.replacementId] : [])],
        message: "This correction links two forms that aren't for the same person, account or payer and year, so it can't replace anything.",
        nextAction: "Link the corrected form to the original it actually replaces.",
      });
      continue;
    }
    valid.push(c);
  }

  const outgoing = new Map<Id, Correction[]>();
  const incoming = new Map<Id, Correction[]>();
  for (const c of valid) {
    outgoing.set(c.originalId, [...(outgoing.get(c.originalId) ?? []), c]);
    if (c.replacementId) incoming.set(c.replacementId, [...(incoming.get(c.replacementId) ?? []), c]);
  }

  // Connected chains: anything with a fork or a cycle is unresolved as a whole.
  const unresolved = new Set<Id>();
  const component = (start: Id): Set<Id> => {
    const seen = new Set<Id>([start]);
    const stack = [start];
    while (stack.length) {
      const id = stack.pop()!;
      for (const c of [...(outgoing.get(id) ?? []), ...(incoming.get(id) ?? [])]) {
        for (const next of [c.originalId, c.replacementId]) {
          if (next && !seen.has(next)) {
            seen.add(next);
            stack.push(next);
          }
        }
      }
    }
    return seen;
  };
  const visited = new Set<Id>();
  for (const id of [...new Set([...outgoing.keys(), ...incoming.keys()])].sort()) {
    if (visited.has(id)) continue;
    const comp = component(id);
    comp.forEach((x) => visited.add(x));
    const fork = [...comp].some((x) => (outgoing.get(x)?.length ?? 0) > 1 || (incoming.get(x)?.length ?? 0) > 1);
    const edges = valid.filter((c) => comp.has(c.originalId));
    // A chain of n forms has n-1 links; more links than that means a cycle.
    const cycle = !fork && edges.filter((c) => c.replacementId !== null).length >= comp.size;
    if (fork || cycle) {
      comp.forEach((x) => unresolved.add(x));
      const sample = formById.get([...comp].sort()[0]!)!;
      issues.add({
        code: fork ? "CORRECTION_FORK" : "CORRECTION_CYCLE",
        severity: "blocker",
        module: moduleForSource(sample),
        personId: sample.personId,
        refs: [...comp].sort(),
        message: fork
          ? "One form is marked as replaced by two different forms (or two forms replace the same one), so we can't tell which is current."
          : "These forms are each marked as replacing the other, so none of them can be current.",
        nextAction: "Keep one straight line of corrections: each form replaced by at most one newer form.",
      });
    }
  }
  unresolved.forEach((id) => exclude(id, "chain_unresolved"));

  // Walk each straight chain from its first form. Confirmed links move the current form forward; the first
  // unconfirmed link stops there, and every later form waits.
  const replacementOf = new Set<Id>();
  for (const c of valid) if (c.replacementId) replacementOf.add(c.replacementId);
  const roots = [...outgoing.keys()].filter((id) => !incoming.has(id) && !unresolved.has(id)).sort();
  for (const root of roots) {
    let at: Id = root;
    let blocked = false;
    for (;;) {
      const c = outgoing.get(at)?.[0];
      if (!c) break;
      const o = formById.get(c.originalId)!;
      if (blocked) {
        if (c.replacementId) exclude(c.replacementId, "replacement_pending");
      } else if (correctionCurrent(c, formById)) {
        exclude(c.originalId, c.replacementId === null ? "voided" : "superseded");
      } else {
        blocked = true;
        if (c.replacementId) exclude(c.replacementId, "replacement_pending");
        issues.add({
          code: c.status === "confirmed" ? "CORRECTION_STALE" : "CORRECTION_UNCONFIRMED",
          severity: "review",
          module: moduleForSource(o),
          personId: o.personId,
          refs: [c.id, c.originalId, ...(c.replacementId ? [c.replacementId] : [])],
          message:
            c.status === "confirmed"
              ? `One of these forms changed after you confirmed the correction, so ${formName(o)} is still being used.`
              : `${formName(o)} is still being used until you confirm the ${c.replacementId ? "corrected form replaces it" : "form was voided"}.`,
          nextAction: "Check both forms and confirm the correction.",
        });
      }
      if (!c.replacementId) break;
      at = c.replacementId;
    }
  }

  // A form printed CORRECTED must say which form it replaces, or be confirmed as the only one received.
  for (const f of forms) {
    if (!f.correctedAsPrinted || replacementOf.has(f.id) || excluded.has(f.id)) continue;
    if (decisions.some((d) => d.kind === "corrected_form_only" && d.sourceId === f.id)) continue;
    exclude(f.id, "corrected_unlinked");
    issues.add({
      code: "CORRECTED_FORM_UNLINKED",
      severity: "blocker",
      module: moduleForSource(f),
      personId: f.personId,
      refs: [f.id],
      message: `${formName(f)} is marked CORRECTED, but it isn't linked to the form it replaces.`,
      nextAction: "Link it to the original form, or confirm it's the only version you received.",
    });
  }

  // Two current forms for the same account or payer are never both counted without the user's say-so.
  const candidates = forms.filter((f) => !excluded.has(f.id));
  const groups = new Map<string, InfoReturn[]>();
  for (const f of candidates) {
    const key = f.kind === "form_1099b" ? `b|${f.accountId}|${f.personId}|${f.description.trim().toLowerCase()}` : `${f.kind}|${f.firmId}|${f.personId}|${f.issuer.trim().toLowerCase()}`;
    groups.set(key, [...(groups.get(key) ?? []), f]);
  }
  for (const group of groups.values()) {
    if (group.length < 2) continue;
    const ids = group.map((g) => g.id).sort();
    const confirmed = decisions.some((d) => d.kind === "confirm_both_info_returns" && ids.every((id) => d.sourceIds.includes(id)));
    if (confirmed) continue;
    ids.forEach((id) => exclude(id, "duplicate_unresolved"));
    issues.add({
      code: "DUPLICATE_INFO_RETURN",
      severity: "blocker",
      module: moduleForSource(group[0]!),
      personId: group[0]!.personId,
      refs: ids,
      message: `There are ${group.length} ${FORM_NAME[group[0]!.kind]} forms for the same ${group[0]!.kind === "form_1099b" ? "account" : "payer"}. They're not added together.`,
      nextAction: "Say whether one corrects the other, one is a duplicate to remove, or both are separate forms.",
    });
  }

  return {
    all,
    effectiveForms: forms.filter((f) => !excluded.has(f.id)),
    excluded: [...excluded].map(([sourceId, reason]) => ({ sourceId, reason })).sort((a, b) => (a.sourceId < b.sourceId ? -1 : 1)),
  };
}
